import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// How long a file upload may run before the browser gives up. The clock covers
// the upload itself, and from a slow link that alone can be minutes: 34 MB took
// 5-6 minutes from the office, and the server accepts up to 100 MB per file.
// The reply comes seconds after the last byte (the job is only queued), so this
// is not a wait on the AI.
const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

// Customers
export const getCustomers = () => axios.get(`${API_URL}/customers`);
export const getCustomer = (id) => axios.get(`${API_URL}/customers/${id}`);
export const createCustomer = (data) => axios.post(`${API_URL}/customers`, data);
export const updateCustomer = (id, data) => axios.put(`${API_URL}/customers/${id}`, data);
export const deleteCustomer = (id) => axios.delete(`${API_URL}/customers/${id}`);

// Projects
export const getProjects = (customerId = null) => {
  const params = customerId ? { customer_id: customerId } : {};
  return axios.get(`${API_URL}/projects`, { params });
};
export const getProject = (id) => axios.get(`${API_URL}/projects/${id}`);
export const createProject = (data) => axios.post(`${API_URL}/projects`, data);
export const updateProject = (id, data) => axios.put(`${API_URL}/projects/${id}`, data);
export const deleteProject = (id) => axios.delete(`${API_URL}/projects/${id}`);

// GitHub Import
export const importFromGithub = (data) => axios.post(`${API_URL}/documentation/import-github`, data, {
  timeout: 180000 // 3 min timeout for clone + AI processing
});

// Azure DevOps Import
export const importFromAzureDevOps = (data) => axios.post(`${API_URL}/documentation/import-azure-devops`, data, {
  timeout: 180000 // 3 min timeout for clone + AI processing
});

// Bitbucket Import
export const importFromBitbucket = (data) => axios.post(`${API_URL}/documentation/import-bitbucket`, data, {
  timeout: 180000 // 3 min timeout for clone + AI processing
});

// GitLab Import
export const importFromGitlab = (data) => axios.post(`${API_URL}/documentation/import-gitlab`, data, {
  timeout: 180000 // 3 min timeout for clone + AI processing
});

// Documentation Feedback
export const submitDocFeedback = (docId, data) => axios.post(`${API_URL}/documentation/${docId}/feedback`, data);
// AI feedback resolution (propose-only — result must be explicitly applied)
export const resolveDocFeedback = (docId) => axios.post(`${API_URL}/documentation/${docId}/feedback/resolve`);
export const getSuggestChangesStatus = (jobId) => axios.get(`${API_URL}/chat/suggest-changes/${jobId}`);
export const applyDocContent = (docId, content, expectedVersion) => axios.patch(`${API_URL}/documentation/${docId}/content`, { content, expected_version: expectedVersion });
export const getDocFeedback = (docId) => axios.get(`${API_URL}/documentation/${docId}/feedback`);
export const updateFeedbackStatus = (docId, feedbackId, status) => axios.patch(`${API_URL}/documentation/${docId}/feedback/${feedbackId}`, { status });

// Chat FAQ / Insights
export const getProjectFaq = (projectId) => axios.get(`${API_URL}/chat/faq?project_id=${projectId}`);

// LLM API Key Vault (admin-only). Keys are write-only — responses never include plaintext.
export const getLlmKeys = () => axios.get(`${API_URL}/settings/llm-keys`);
export const setLlmKey = (provider, key) => axios.put(`${API_URL}/settings/llm-keys/${provider}`, { key });
export const deleteLlmKey = (provider) => axios.delete(`${API_URL}/settings/llm-keys/${provider}`);
export const testLlmKey = (provider) => axios.post(`${API_URL}/settings/llm-keys/${provider}/test`, {}, { timeout: 60000 });

// Documentation
export const getDocumentation = (projectId = null, customerId = null) => {
  const params = {};
  if (projectId) params.project_id = projectId;
  if (customerId) params.customer_id = customerId;
  return axios.get(`${API_URL}/documentation`, { params });
};
export const getDocumentationById = (id) => axios.get(`${API_URL}/documentation/${id}`);
export const uploadDocumentation = (formData, onUploadProgress) => axios.post(`${API_URL}/documentation/upload`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});
// Multi-source upload: several files/zips + an optional linked repo -> ONE document
export const uploadDocumentationMulti = (formData, onUploadProgress) => axios.post(`${API_URL}/documentation/upload-multi`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});
export const deleteDocumentation = (id) => axios.delete(`${API_URL}/documentation/${id}`);
export const exportDocumentationWord = (id) => `${API_URL}/documentation/${id}/export-word`;
export const regenerateDocumentation = (id) => axios.post(`${API_URL}/documentation/${id}/regenerate`, {}, {
  timeout: 300000 // 5 min timeout for AI regeneration
});

// SOW (Scope of Work) Drafts
export const createSowDraft = (formData, onUploadProgress) => axios.post(`${API_URL}/sow/create`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});
export const getSowDraft = (docId) => axios.get(`${API_URL}/sow/${docId}`);
// answers: {question: answer} for the draft's follow-up questions (omit to reuse the saved ones)
export const regenerateSow = (docId, answers) => axios.post(`${API_URL}/sow/${docId}/regenerate`, answers ? { answers } : {}, {
  timeout: 300000 // 5 min timeout for AI regeneration
});
export const saveSowAnswers = (docId, answers) => axios.patch(`${API_URL}/sow/${docId}/answers`, { answers });
export const exportSowWord = (id) => `${API_URL}/documentation/${id}/export-sow-word`;
export const exportSowPdf = (id) => `${API_URL}/documentation/${id}/export-sow-pdf`;
// Design / change-request docs have their own branded exports — the generic
// export-word/-pdf endpoints 400 on doc_kind='design' by design.
export const exportDesignWord = (id) => `${API_URL}/documentation/${id}/export-design-word`;
export const exportDesignPdf = (id) => `${API_URL}/documentation/${id}/export-design-pdf`;

// SOW Discovery Questionnaire
export const createQuestionnaire = (formData, onUploadProgress) => axios.post(`${API_URL}/sow/questions`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});
export const listQuestionnaires = (params) => axios.get(`${API_URL}/sow/questions`, { params });
export const getQuestionnaire = (id) => axios.get(`${API_URL}/sow/questions/${id}`);
export const saveQuestionnaire = (id, data) => axios.patch(`${API_URL}/sow/questions/${id}`, data);
export const generateFromQuestionnaire = (id) => axios.post(`${API_URL}/sow/questions/${id}/generate`, {}, {
  timeout: 300000 // 5 min timeout for AI drafting
});
// Export URL — append ?format=md|docx. Fetch with the auth header for a blob/text download.
export const exportQuestionnaire = (id, format) => `${API_URL}/sow/questions/${id}/export?format=${format}`;

// Design Documentation / Change Request Documents
// The multipart payload carries doc_type ('design' | 'change_request'), the
// ticket files, and — when the consultant supplies it — either a code zip under
// the same `files` key or a repository to clone. Long timeout: the optional
// codebase is analysed before the design is written.
export const createDesignDoc = (formData, onUploadProgress) => axios.post(`${API_URL}/design/create`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});
export const getDesignDoc = (docId) => axios.get(`${API_URL}/design/${docId}`);
// answers: {question: answer} for the draft's follow-up questions (omit to reuse the saved ones)
export const regenerateDesignDoc = (docId, answers) => axios.post(`${API_URL}/design/${docId}/regenerate`, answers ? { answers } : {}, {
  timeout: 300000 // 5 min timeout for AI regeneration
});
export const saveDesignAnswers = (docId, answers) => axios.patch(`${API_URL}/design/${docId}/answers`, { answers });
// The project's latest Code Docs — a change request reads them; empty = document the code first.
export const getProjectCodeDocs = (projectId) => axios.get(`${API_URL}/design/code-docs/${projectId}`);

// Import a finished document saved as a DocHub draft file (.json): a New Design
// SOW (doc_kind 'sow') or a change request / design proposal (doc_kind 'design').
// The body is the file's JSON with the chosen project_id. 400/422 = refused, with
// the list of what to fix; success returns the new document's id and doc_kind,
// plus any warnings. The one place the endpoint is named.
const IMPORT_DRAFT_URL = `${API_URL}/documentation/import-draft`;
export const importDraftDocument = (payload) => axios.post(IMPORT_DRAFT_URL, payload, {
  timeout: 120000 // the checks and the markdown render run before it replies
});

// Code Documentation — one submission, TWO documents (Code Analysis Report and
// Integration Reference Manual). The response carries only the job id; the
// documentation ids arrive on the completed job.
export const createCodeDocs = (formData, onUploadProgress) => axios.post(`${API_URL}/codedoc/create`, formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
  timeout: UPLOAD_TIMEOUT_MS,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
  onUploadProgress
});

// Code documents (doc_kind 'code_analysis_report' / 'integration_reference_manual')
// export through the plain export-word/-pdf endpoints; SOW and design documents
// do not — those endpoints 400 on them. Anywhere a page can show any kind of
// document, use exportDocument below.

// One export URL for every document family (fmt 'word' | 'pdf'): the server
// sends SOW and design documents to their branded builders and everything
// else to the plain exporters, honouring ?audience=.
export const exportDocument = (id, fmt) => `${API_URL}/documentation/${id}/export/${fmt}`;

// Fetch an export with the auth header and save it. Rejects with the server's
// own error detail, so a failed export says why instead of "Export failed".
export const downloadExport = async (url, token, filename) => {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    throw new Error(typeof detail === 'string' && detail ? detail : `Export failed (HTTP ${res.status})`);
  }
  const href = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking straight after click() can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(href), 40000);
};

// Chat
export const sendChatMessage = (question, projectId = null, customerId = null) => {
  return axios.post(`${API_URL}/chat`, { question, project_id: projectId, customer_id: customerId });
};
export const getChatHistory = (projectId = null) => {
  const params = projectId ? { project_id: projectId } : {};
  return axios.get(`${API_URL}/chat/history`, { params });
};

// Search
export const searchDocumentation = (query, filters = {}) => {
  return axios.post(`${API_URL}/search`, { query, ...filters });
};

// Jobs (for async documentation generation)
export const getJobStatus = (jobId) => axios.get(`${API_URL}/jobs/${jobId}`);
export const getJobs = (projectId = null) => {
  const params = projectId ? { project_id: projectId } : {};
  return axios.get(`${API_URL}/jobs`, { params });
};

// Stats
export const getStats = () => axios.get(`${API_URL}/stats`);

// Version History
export const getDocVersions = (docId) => axios.get(`${API_URL}/documentation/${docId}/versions`);
export const getDocVersion = (docId, versionId) => axios.get(`${API_URL}/documentation/${docId}/versions/${versionId}`);
export const diffDocVersions = (docId, v1Id, v2Id) => axios.post(`${API_URL}/documentation/${docId}/diff?v1_id=${v1Id}&v2_id=${v2Id}`);
export const createDocSnapshot = (docId) => axios.post(`${API_URL}/documentation/${docId}/snapshot`);

// Export
export const exportDocumentationPdf = (id) => `${API_URL}/documentation/${id}/export-pdf`;

// Cross-document diff (for comparing regenerated versions)
export const crossDiffDocs = (docId1, docId2) => axios.post(`${API_URL}/documentation/cross-diff?doc_id_1=${docId1}&doc_id_2=${docId2}`);

// Profile & Password
export const updateProfile = (data) => axios.patch(`${API_URL}/auth/profile`, data);
export const changePassword = (data) => axios.post(`${API_URL}/auth/change-password`, data);
export const adminResetPassword = (userId) => axios.post(`${API_URL}/auth/users/${userId}/reset-password`);

// Source Files
export const getSourceFiles = (docId) => axios.get(`${API_URL}/documentation/${docId}/source-files`);
export const getSourceFileContent = (docId, filename) => axios.get(`${API_URL}/documentation/${docId}/source-files/${encodeURIComponent(filename)}`);

// Saved Repo Tokens
export const getSavedTokens = () => axios.get(`${API_URL}/auth/saved-tokens`);
export const deleteSavedToken = (tokenId) => axios.delete(`${API_URL}/auth/saved-tokens/${tokenId}`);

// Analytics (admin only)
export const getAnalyticsDashboard = (days = 30) => axios.get(`${API_URL}/analytics/dashboard?days=${days}`);

// ADK Agentic Chat (Labs / Phase 1)
export const getAdkStatus = () => axios.get(`${API_URL}/adk/status`);
export const adkChat = (data) => axios.post(`${API_URL}/adk/chat`, data, { timeout: 120000 });
